import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Stack,
  alpha,
  Chip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  aiGradientButtonSx,
  getAiOverlayHeaderSx,
  getAiOverlayPaperSx,
  getAiOverlayShellSx,
  getAiOverlayIconBoxSx,
  getAiOverlayBodyTextSx,
  getAiOverlayTitleSx,
  getAiOverlayUploadButtonSx,
  AI_OVERLAY_ACCENT,
  AI_OVERLAY_ACCENT_2,
} from '../../shared';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { extractStockInvoiceData } from '../../services/api';
import { parseInvoiceAmount } from '../../utils/invoiceNumericParse';

const SUPPORTED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.webp', '.xlsx', '.xls', '.csv'];
const ACCEPT_ATTR = '.pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv';

const toNumberOrEmpty = (value) => {
  const n = parseInvoiceAmount(value);
  return n === '' ? '' : n;
};

/** Same shape as StockInvoiceUploadModal — parent applies to form */
export const normalizeStockInvoiceExtract = (raw) => {
  const items = Array.isArray(raw?.items) ? raw.items : [];
  const normalizedItems = items.length
    ? items.map((item) => {
        const qty = toNumberOrEmpty(item?.quantity);
        const amount = toNumberOrEmpty(item?.amount);
        // Rate OR Price → Stock Price (same field). Prefer rate when both exist.
        const rateN = Number(item?.rate);
        const priceN = Number(item?.price ?? item?.unit_price);
        let price =
          Number.isFinite(rateN) && rateN > 0
            ? rateN
            : Number.isFinite(priceN) && priceN > 0
              ? priceN
              : toNumberOrEmpty(item?.price ?? item?.rate);
        const qtyN = Number(qty);
        const amountN = Number(amount);
        // Guard only: if AI put Amount into rate/price, recover unit price.
        if (
          Number.isFinite(qtyN) &&
          qtyN > 1 &&
          Number.isFinite(amountN) &&
          amountN > 0 &&
          Number.isFinite(Number(price)) &&
          Math.abs(Number(price) - amountN) <= Math.max(0.5, amountN * 0.02)
        ) {
          price = Number((amountN / qtyN).toFixed(4));
        }
        const gstRate = toNumberOrEmpty(item?.gst_rate);
        const gstRateN = Number(gstRate);
        const halfGst =
          Number.isFinite(gstRateN) && gstRateN > 0 ? Number((gstRateN / 2).toFixed(3)) : '';
        const cgst = toNumberOrEmpty(item?.cgst);
        const sgst = toNumberOrEmpty(item?.sgst);
        const igst = toNumberOrEmpty(item?.igst);
        return {
          name: String(item?.name || '').trim(),
          quantity: qty,
          unit: String(item?.unit || '').trim(),
          price,
          amount: Number.isFinite(amountN) && amountN > 0 ? amountN : '',
          gst_rate: gstRate,
          cgst: cgst !== '' ? cgst : halfGst,
          sgst: sgst !== '' ? sgst : halfGst,
          igst: igst !== '' ? igst : Number.isFinite(gstRateN) && gstRateN > 0 ? 0 : '',
        };
      })
    : [{ name: '', quantity: '', unit: '', price: '' }];

  return {
    supplier_name: String(raw?.supplier_name || '').trim(),
    invoice_number: String(raw?.invoice_number || '').trim(),
    invoice_date: String(raw?.invoice_date || '').trim(),
    total_amount: toNumberOrEmpty(raw?.total_amount),
    items: normalizedItems,
  };
};

/**
 * Centered AI invoice dialog — dashboard dark theme (teal accent, elevated surface).
 */
function StockAiInvoiceOverlay({ open, onClose, onSuccess, isDark }) {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (!open) {
      setError('');
      setLoading(false);
      setSelectedFile(null);
    }
  }, [open]);

  const handlePickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const fileNameLower = file.name.toLowerCase();
    const ok = SUPPORTED_EXTENSIONS.some((ext) => fileNameLower.endsWith(ext));
    if (!ok) {
      setError('Unsupported file type. Use PDF, image, or spreadsheet.');
      setSelectedFile(null);
      return;
    }
    setError('');
    setSelectedFile(file);
  };

  const runExtraction = useCallback(async () => {
    if (!selectedFile) {
      setError('Choose an invoice file first.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const response = await extractStockInvoiceData(selectedFile);
      const extracted = normalizeStockInvoiceExtract(response?.extracted_data || {});
      onSuccess(extracted);
    } catch (e) {
      const msg =
        e?.userMessage ||
        e?.response?.data?.error ||
        e?.message ||
        'Could not read this invoice';
      setError(typeof msg === 'string' ? msg : 'Could not read this invoice');
    } finally {
      setLoading(false);
    }
  }, [selectedFile, onSuccess]);

  if (!open) return null;

  const accent = AI_OVERLAY_ACCENT;
  const accent2 = AI_OVERLAY_ACCENT_2;
  const bodyTextSx = getAiOverlayBodyTextSx(isDark);
  const titleSx = getAiOverlayTitleSx(isDark);

  return (
    <Box
      sx={getAiOverlayShellSx(isDark, {
        position: 'fixed',
        zIndex: (theme) => theme.zIndex.modal + 2,
        centered: true,
      })}
    >
      <Paper elevation={0} sx={getAiOverlayPaperSx(isDark, accent, accent2)}>
        <Box sx={getAiOverlayHeaderSx(accent, accent2, isDark)}>
          <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" minWidth={0}>
            <Box sx={getAiOverlayIconBoxSx(accent)}>
              <AutoAwesomeIcon sx={{ color: accent, fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                <Typography variant="subtitle1" sx={titleSx}>
                  AI Invoice
                </Typography>
                <Chip
                  size="small"
                  label="Gemini"
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    bgcolor: alpha(accent, isDark ? 0.14 : 0.1),
                    color: accent,
                    border: `1px solid ${alpha(accent, 0.28)}`,
                  }}
                />
              </Stack>
              <Typography variant="caption" sx={{ ...bodyTextSx, display: 'block', mt: 0.25 }}>
                Extract supplier, totals, and line items
              </Typography>
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={loading}
            aria-label="Close AI invoice"
            sx={{ color: isDark ? 'rgba(148, 163, 184, 0.92)' : '#64748b' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: 2.5 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            hidden
            onChange={handlePickFile}
          />

          {loading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                sx={{
                  height: 4,
                  borderRadius: 2,
                  bgcolor: alpha(accent, 0.12),
                  '& .MuiLinearProgress-bar': {
                    background: `linear-gradient(90deg, ${accent2} 0%, ${accent} 100%)`,
                  },
                }}
              />
              <Typography variant="body2" sx={{ ...bodyTextSx, mt: 1.5, textAlign: 'center' }}>
                Reading invoice with AI…
              </Typography>
            </Box>
          )}

          {!loading && (
            <>
              <Box
                role="note"
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1.75,
                  border: `1px solid ${alpha('#f59e0b', isDark ? 0.42 : 0.34)}`,
                  bgcolor: alpha('#f59e0b', isDark ? 0.08 : 0.07),
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <InfoOutlinedIcon sx={{ color: '#f59e0b', fontSize: 18, flexShrink: 0 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      ...bodyTextSx,
                      lineHeight: 1.5,
                      color: isDark ? 'rgba(226,232,240,0.9)' : '#475569',
                    }}
                  >
                    AI is still in training — accuracy keeps improving. Please review and edit
                    fields before saving.
                  </Typography>
                </Stack>
              </Box>

              <Typography variant="body2" sx={{ ...bodyTextSx, mb: 2, lineHeight: 1.55 }}>
                Upload one invoice (PDF, image, Excel, or CSV). We&apos;ll fill supplier, invoice details, and line
                items.
              </Typography>

              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  border: `1px dashed ${alpha(accent, isDark ? 0.35 : 0.28)}`,
                  bgcolor: isDark ? alpha(accent, 0.04) : alpha(accent, 0.03),
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
                  <Typography variant="subtitle2" sx={{ ...titleSx, fontSize: '0.9rem' }}>
                    Invoice file
                    <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>
                      *
                    </Typography>
                  </Typography>
                  {selectedFile && (
                    <Chip
                      size="small"
                      icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                      label="Added"
                      color="success"
                      variant="outlined"
                      onDelete={() => setSelectedFile(null)}
                    />
                  )}
                </Stack>
                <Button
                  fullWidth
                  variant="outlined"
                  size="medium"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                  sx={getAiOverlayUploadButtonSx(isDark, accent)}
                >
                  Upload
                </Button>
                <Typography variant="body2" sx={{ ...bodyTextSx, mt: 1, wordBreak: 'break-word' }}>
                  {selectedFile ? selectedFile.name : 'No file selected'}
                </Typography>
                <Typography variant="caption" sx={{ ...bodyTextSx, display: 'block', mt: 0.75, fontSize: '0.72rem' }}>
                  Supported: {SUPPORTED_EXTENSIONS.join(', ')}
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !selectedFile}
                startIcon={<AutoAwesomeIcon />}
                onClick={runExtraction}
                sx={aiGradientButtonSx({
                  mt: 0.5,
                  py: 1.35,
                })}
              >
                Read with AI & fill form
              </Button>
            </>
          )}

          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default StockAiInvoiceOverlay;
